import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, X } from 'lucide-react';
import { ApplicantPhoto } from './ApplicantPhoto';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']];
const NOTIFY = { whatsapp_sent: 'WhatsApp', sms_sent: 'SMS', failed: 'Not sent', pending: 'Sending…' };

export const ApplicationsPanel = ({ token, onUnauthorized, onApproved }) => {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState('');
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    axios.get(`${API}/admin/applications`, { params: { status }, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch((e) => { if (e?.response?.status === 401) onUnauthorized(); });
  }, [status, token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const act = async (a, action) => {
    if (action === 'reject' && !window.confirm(`Reject application from "${a.name}"?`)) return;
    setBusyId(a.id);
    try {
      await axios.post(`${API}/admin/applications/${a.id}/${action}`, {}, headers);
      if (action === 'approve') onApproved();
      load();
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized();
      else window.alert(e?.response?.data?.detail || 'Action failed');
    } finally { setBusyId(''); }
  };

  return (
    <section className="jbss-visit-stats" data-testid="admin-applications">
      <div className="jbss-app-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} className={`jbss-donate-chip ${status === key ? 'jbss-donate-chip-active' : ''}`}
            onClick={() => setStatus(key)} data-testid={`admin-applications-tab-${key}`}>
            {label} ({data?.counts?.[key] ?? 0})
          </button>
        ))}
      </div>
      {data && data.applications.length === 0 && <p className="jbss-no-results" data-testid="admin-applications-empty">No {status} applications.</p>}
      <div className="jbss-admin-table jbss-donation-list">
        {data?.applications.map((a) => (
          <div className="jbss-admin-trow" key={a.id} data-testid={`admin-application-${a.id}`}>
            <ApplicantPhoto app={a} token={token} onChanged={load} onUnauthorized={onUnauthorized} />
            <div className="jbss-admin-tinfo jbss-app-grow">
              <span className="jbss-admin-tname">{a.name} · {a.phone}</span>
              <span className="jbss-admin-tmeta">{[a.place, a.occupation, new Date(a.created_at).toLocaleDateString('en-IN')].filter(Boolean).join(' · ')}</span>
              {a.additional_info && <span className="jbss-admin-tmeta jbss-app-info">{a.additional_info}</span>}
              {a.member_no && (
                <span className="jbss-admin-tmeta" data-testid={`admin-application-memberno-${a.id}`}>
                  {a.member_no} · Welcome msg: {NOTIFY[a.notify_status] || '—'}
                </span>
              )}
            </div>
            {status === 'approved' && (
              <a className="jbss-admin-link" href={`${API}/membership/${a.id}/card`} target="_blank" rel="noreferrer"
                data-testid={`admin-idcard-${a.id}`}>ID Card</a>
            )}
            {status === 'pending' && (
              <div className="jbss-admin-trow-actions">
                <button className="jbss-icon-btn" disabled={busyId === a.id} onClick={() => act(a, 'approve')} data-testid={`admin-approve-${a.id}`} aria-label="Approve"><Check size={16} /></button>
                <button className="jbss-icon-btn jbss-icon-danger" disabled={busyId === a.id} onClick={() => act(a, 'reject')} data-testid={`admin-reject-${a.id}`} aria-label="Reject"><X size={16} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
